import { Button } from '@thanx/uikit/button'
import {
  useGetAvailableRewardsQuery,
  useGetRecoveryConfigQuery,
} from 'api/recoveryAi'
import type { RecoveryConfig } from 'api/recoveryAi.types'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useMemo, useState } from 'react'
import { useStyletron } from 'styletron-react'
import { useAbility } from 'utilities/ability'
import { AgentSettingsCard, AgentSettingsRow } from './AgentSettingsCard'
import { AGENT_TUNING_ROW_KEYS, RowKey } from './configRowKeys'
import RecoveryConfigModals, { useOpenRowState } from './RecoveryConfigModals'
import { useMakeGoodCuration } from './useMakeGoodCuration'

// Re-exported for the two simulators, which type their lifted open-row state
// against this component's controlled contract.
export type { RowKey }

// Row label resolves from the settings tab labels, the same source the dialog
// titles use (one source for "Brand voice" / "Recovery generosity" / …).
const tTabs = buildTranslate('recovery_ai.settings.tabs')
const tModal = buildTranslate('recovery_ai.config_modal')
// Current-value labels shown inline on each chevron row (AI-167). Reused from
// the read-only AgentSettingsSummary so the two never drift: brand-voice mode +
// reward count from agent_settings, generosity posture from decision_log.
const tAgentSettings = buildTranslate('recovery_ai.sandbox.agent_settings')
const tPosture = buildTranslate('recovery_ai.sandbox.decision_log')
const tApproved = buildTranslate('recovery_ai.settings.approved_rewards')

// The Approved-rewards modal is ~20% wider than the other agent-tuning modals
// (Figma parity) — it carries two reward lists side by their controls.
const APPROVED_REWARDS_MODAL_WIDTH = '800px'

// The generosity modal's two-column stance detail reads as cramped at the
// shared 600px default (Figma node 2318:65820's "Sandbox" frame specs it at
// 1000px).
const GENEROSITY_MODAL_WIDTH = '1000px'

// Resolve the current value shown inline on a chevron row (AI-167). Returns
// undefined when the relevant config field is absent so the row degrades to a
// label-only display (e.g. while the config GET is still in flight).
const rowValue = (
  key: RowKey,
  config: Pick<
    RecoveryConfig,
    'brand_voice_mode' | 'philosophy_posture' | 'approved_reward_ids'
  >,
  activeRewardIds?: Set<string>,
  // Visible make-good ladder offers to fold into the count. They add to the brand
  // rewards without double-counting: `activeRewardIds` already excludes
  // ai_generated ids, so a make-good in approved_reward_ids is counted here only
  // (as a ladder offer), never also in brandCount.
  visibleMakeGoodCount = 0
): string | undefined => {
  switch (key) {
    case 'brand_voice':
      return config.brand_voice_mode
        ? tAgentSettings(`brand_voice_values.${config.brand_voice_mode}`)
        : undefined
    case 'recovery_generosity':
      return config.philosophy_posture
        ? tPosture(`posture_values.${config.philosophy_posture}`)
        : undefined
    case 'approved_rewards': {
      const approvedIds = config.approved_reward_ids ?? []
      // Count only rewards that are still ACTIVE and approved — the "grantable"
      // set the simulator actually renders a card for (mirrors useSimulatorData's
      // filter). Otherwise this row could say "3 rewards" while the live preview
      // beside it shows 2 cards after one was deactivated via /rewards. Until the
      // rewards catalog resolves (activeRewardIds undefined) fall back to the
      // approved-id count.
      const brandCount = activeRewardIds
        ? approvedIds.filter(id => activeRewardIds.has(id)).length
        : approvedIds.length
      // ...plus the make-good offers the ladder actually shows (curation on).
      // The row owns BOTH pickers, so its count must total both.
      const count = brandCount + visibleMakeGoodCount
      return tAgentSettings('rewards', { count })
    }
    // Escalation rules has no single inline summary (it carries two thresholds)
    // and is an advanced row, so it renders label-only. The two adapter rows
    // never appear on this tab.
    case 'escalation_rules':
    case 'active_locations':
    case 'escalation_contacts':
      return undefined
  }
}

// R12 (AI-168) integration: the simulator's "Agent Tuning" chevron rows — the
// trigger chrome for the shared config-modal registry. This component owns the
// rows, their inline current values and the "Advanced" disclosure; every dialog
// downstream of a row opening belongs to RecoveryConfigModals. Mounted into the
// agent_tuning tab by FE-4 (Landing/index.tsx). "Save and test" doesn't navigate
// — the simulator is on this same tab — so the registry closes the modal and,
// via the onTest callback Landing passes, scrolls the RecoverySandbox into view.
type Props = {
  // Scroll the on-tab simulator into view after a "Save and test". Landing owns
  // the sandbox ref and passes this; optional so the component still renders
  // standalone (e.g. in tests) where there's nothing to scroll to.
  onTest?: () => void
  // Controlled open-row state (C3). When a host supplies onOpenRow the open row is
  // lifted into it, so the simulator's "Tweak …" deep-link can open a row from outside
  // this component. That host is WizardSimulator — the lift used to live in
  // Landing/index.tsx and was moved in 8e543ce16 (#3878). When BOTH are omitted the
  // component falls back to self-managed open state (the uncontrolled behavior the
  // Agent Tuning tab uses).
  openRow?: RowKey | null
  onOpenRow?: (key: RowKey | null) => void
  /**
   * Hide the "Advanced" escalation-rules disclosure. The wizard sets this
   * because escalation rules are a POST-activation setting: EscalationRulesPane
   * renders `escalation_rules_locked` instead of its editor while
   * `!onboarding_completed`, and the whole wizard runs pre-activation — so the
   * row could only ever open onto a locked pane.
   *
   *   context: NOT because Finalize writes those fields. It does not, and never
   *   did — mapStepToConfig has no case for them, so the older "the wizard's own
   *   escalations stage sets them at Finalize" rationale was false in both its
   *   forms. The wizard does carry frequentThreshold / frequencyPeriod /
   *   repeatedThreshold in WizardState and hydrates all three, but the only
   *   component that edits them is the orphaned LimitsGuardrails step, retained
   *   for WP2 (AI-173) and wired into no route. So they are live state with no
   *   reachable editor and no writer — worth resolving with WP2 rather than
   *   leaving as an implied second writer.
   */
  hideAdvancedRow?: boolean
  /**
   * Hide "Save and test". The wizard sets this — its simulator is already on the
   * same page, so there is nowhere to route. Plain Save still commits live.
   */
  hideSaveAndTest?: boolean
  /**
   * Fired with the committed fields after a successful PUT. The wizard mirrors
   * them into WizardContext so its Finalize, which re-sends philosophy_posture /
   * approved_reward_ids unconditionally from once-hydrated state, cannot clobber
   * an edit made here.
   */
  onCommitted?: (staged: Partial<RecoveryConfig>) => void
  /**
   * Whether a successful save also reloads this surface's simulator conversation, so
   * the post-save banner can say so. Default false, which keeps every existing caller
   * on the original copy. Forwarded to the registry, which owns the banner.
   *
   * A prop rather than something inferred, because it varies WITHIN a caller: the
   * wizard passes `isFree`, since its guided phase deliberately does not restart.
   */
  conversationRestartsOnSave?: boolean
}

const AgentTuningConfigModals = ({
  onTest,
  openRow: controlledOpenRow,
  onOpenRow,
  hideAdvancedRow = false,
  hideSaveAndTest = false,
  onCommitted,
  conversationRestartsOnSave = false,
}: Props) => {
  const [css] = useStyletron()
  // AI-64: admin-only writes. A view-only operator can reach the Agent Tuning tab
  // (RBAC can-view) and must still be able to READ each setting's current value and
  // talk to the phone simulator — but every drill-in row is an editor entry point,
  // so the rows render inert for them. Same CASL gate the Settings panes apply.
  const ability = useAbility()
  const canManage = ability.can('manage', 'MerchantSettings')
  // Resolved once here — the chevron rows below need the setter, so this host
  // owns the controlled/uncontrolled decision and hands the registry a
  // controlled pair.
  const { openRow, setOpenRow } = useOpenRowState(
    'AgentTuningConfigModals',
    controlledOpenRow,
    onOpenRow
  )
  // AI-64: Escalation Rules is a power-user setting with a sensible default, so
  // it lives under a collapsed "Advanced" disclosure rather than a primary row.
  const [advancedOpen, setAdvancedOpen] = useState(false)
  // Rewards can be deactivated from the separate /rewards surface (which never
  // invalidates this cache), so force a refetch on mount — mirrors the pane.
  // Also warms the cache the registry reads, so opening the approved-rewards row
  // resolves without a spinner.
  const rewardsQuery = useGetAvailableRewardsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  })
  // Drives the inline current-value on each chevron row (AI-167). Read-only here
  // — the editing/commit path is the registry's per-open draft provider. A modal
  // commit invalidates the 'RecoveryConfig' tag, so these row values re-render
  // with the new config automatically (no manual refresh).
  const { data: config } = useGetRecoveryConfigQuery()
  // Only for the approved-rewards row's total; the curation controls themselves
  // live inside the modal.
  const makeGood = useMakeGoodCuration()

  // Ids of the merchant's own (non-make-good) rewards still active in the catalog.
  // The approved-rewards row counts the intersection of approved_reward_ids with
  // these (the grantable brand set the engine renders cards for), so the inline
  // count never drifts from the preview beside it. ai_generated make-goods are
  // EXCLUDED here and counted once via visibleCount — otherwise a grandfathered
  // config whose make-good id sits in approved_reward_ids would count that reward
  // twice (once as brand, once as a materialized ladder offer). Undefined until
  // the catalog resolves — rowValue then falls back to the raw approved-id count.
  const activeRewardIds = useMemo(
    () =>
      rewardsQuery.data
        ? new Set(
            rewardsQuery.data
              .filter(r => r.active && !r.ai_generated)
              .map(r => r.id)
          )
        : undefined,
    [rewardsQuery.data]
  )

  // Hiding the Advanced disclosure withholds the row from the registry too, so
  // the wizard cannot open an editor onto a pane that is locked pre-activation.
  const rows: RowKey[] = useMemo(
    () =>
      hideAdvancedRow
        ? AGENT_TUNING_ROW_KEYS
        : [...AGENT_TUNING_ROW_KEYS, 'escalation_rules'],
    [hideAdvancedRow]
  )

  // One chevron row (label + inline current value + open-modal affordance),
  // shared by the primary rows and the Advanced escalation row so they never
  // drift. The chrome comes from AgentSettingsCard, which the read-only summary
  // also uses.
  const renderRow = (key: RowKey) => {
    // Inline current value (AI-167) — sits under the label so each row's
    // accessible name reads "<label> <value>" (the chevron is decorative).
    const value = config
      ? rowValue(key, config, activeRewardIds, makeGood.visibleCount)
      : undefined
    return (
      <AgentSettingsRow
        key={key}
        label={tTabs(key)}
        value={value}
        disabled={!canManage}
        // Guarded as well as disabled: `disabled` is a render detail of this row,
        // and setOpenRow is the one call that puts an editor on screen. Belt and
        // braces here is cheap; the modal's own save is separately gated on the
        // same ability via the draft provider's adapter (ConfigRowModal).
        onClick={() => {
          if (canManage) setOpenRow(key)
        }}
      />
    )
  }

  return (
    <>
      {/* First in the tree because it renders the post-save confirmation, which
          belongs above the rows the merchant returns to. */}
      <RecoveryConfigModals
        rows={rows}
        instanceId="agent-tuning"
        openRow={openRow}
        onOpenRow={setOpenRow}
        hideSaveAndTest={hideSaveAndTest}
        onTest={onTest}
        onCommitted={onCommitted}
        conversationRestartsOnSave={conversationRestartsOnSave}
        // Approved rewards is the redesigned surface: "Save changes" copy + the
        // wider modal (Figma parity). Generosity is wider too. The other rows
        // keep the default "Save"/600px.
        rowWidths={{
          approved_rewards: APPROVED_REWARDS_MODAL_WIDTH,
          recovery_generosity: GENEROSITY_MODAL_WIDTH,
        }}
        rowSaveLabels={{ approved_rewards: tApproved('save_changes') }}
      />

      {/* ONE card with hairline-separated rows, not three bordered cards with 12px of
          page background between them (plan §1.6). The rows sit flush, so the block is
          177px instead of 270px and the three settings read as one group. */}
      <AgentSettingsCard
        title={tAgentSettings('title')}
        description={tAgentSettings('description')}
      >
        {AGENT_TUNING_ROW_KEYS.map(renderRow)}
      </AgentSettingsCard>

      {/* AI-64: Escalation Rules is a power-user setting with a sensible default,
          so it's tucked behind a collapsed "Advanced" disclosure rather than a
          primary row (too much detail for the average merchant). Hidden in the
          wizard because the pane it opens is locked pre-activation — see
          hideAdvancedRow, and note the wizard does NOT persist these fields. */}
      {!hideAdvancedRow && (
        <div className={css({ marginTop: '16px' })}>
          <Button
            kind="minimal"
            aria-expanded={advancedOpen}
            aria-controls="agent-tuning-advanced-rows"
            onClick={() => setAdvancedOpen(open => !open)}
          >
            {tModal('advanced')}
            <i
              className={`fa fa-chevron-${advancedOpen ? 'up' : 'down'} ${css({
                marginLeft: '8px',
                color: palette.grey70,
              })}`}
              aria-hidden="true"
            />
          </Button>
          {advancedOpen && (
            <div
              id="agent-tuning-advanced-rows"
              className={`d-flex flex-column ${css({
                gap: '12px',
                marginTop: '8px',
              })}`}
            >
              {renderRow('escalation_rules')}
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default AgentTuningConfigModals
